import React, { useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { FilterBox } from "../Boxes";
import {
  BuildingDropdown,
  ConsumptionOrCo2Dropdown,
  CustomersCountryDropdown,
  EnergyReportTypeDropdown,
  EnergyTypeDropdown,
  MeterDropdown,
  MonthsDropdown,
  MultiDatePickerDmy,
  SegmentDropDown,
  SelectDropdown,
  SingleDatePickerDmy,
  StatusDropdown,
  YearPicker,
} from "@/components";

import { instanceOf, string, bool, func } from "prop-types";
import {
  getMinDate,
  getMaxDate,
  getStartAndEndDate,
  segmentList,
} from "@/utils";
import { useDateRangeSettings } from "@/hooks";
import { useUser } from "@/app/providers";
import { isEqual } from "lodash";

type FilterBar1Props = Record<string, any> & {
  getSelectedValues: (values: Record<string, any>) => void;
};

type SegmentKey = keyof ReturnType<typeof segmentList>;

type FilterBar1Defaults = {
  country: any;
  building: any;
  meter: any;
  energyType: any;
  shortSegment: any;
  reportType: any;
  selectedDate: {
    startDate: Date;
    endDate: Date;
  };
  singleDate: Date;
  selectedYearDate: Date;
  selectedMonth: string;
  segmentName: string;
  consumptionOrCo2: any;
  showCountry: boolean;
  showBuilding: boolean;
  showMeter: boolean;
  showEnergyType: boolean;
  showDateSingle: boolean;
  showDateMultiple: boolean;
  showYear: boolean;
  showMonth: boolean;
  showBuildingAll: boolean;
  showMeterAll: boolean;
  showEnergyTypeAll: boolean;
  showGasUnit: boolean;
  showGasUnitDay: boolean;
  showShortSegment: boolean;
  showReportType: boolean;
  showConsumptionOrCo2: boolean;
  showStatus: boolean;
  status: any;
};

export const FilterBar1 = (props: FilterBar1Props) => {
  const { user } = useUser();
  const segments = segmentList() as Record<
    SegmentKey,
    { shortSegment: string; dateFormat: string }
  >;

  let defaultStartDate = new Date();
  let defaultEndDate = new Date();

  defaultStartDate.setHours(0, 0, 0, 0);
  defaultStartDate.setDate(1);
  defaultStartDate.setMonth(defaultStartDate.getMonth() - 1);

  defaultEndDate.setHours(23, 59, 59, 999);
  defaultEndDate.setDate(1);
  defaultEndDate.setDate(defaultEndDate.getDate() - 1);

  //region React Hooks (useState)
  const propsDefault: FilterBar1Defaults = {
    // currentTeam: undefined,
    // currentTeamId: undefined,
    // currentSubsidiary: undefined,
    // currentSubsidiaryId: undefined,
    country: undefined,
    building: undefined,
    meter: undefined,
    energyType: "",
    shortSegment: "m",
    reportType: "",
    selectedDate: {
      startDate: defaultStartDate,
      endDate: defaultEndDate,
    },
    singleDate: defaultStartDate,
    selectedYearDate: defaultStartDate,
    selectedMonth: "jan",
    segmentName: "year",
    consumptionOrCo2: "consumption",
    showCountry: false,
    showBuilding: false,
    showMeter: false,
    showEnergyType: false,
    showDateSingle: false,
    showDateMultiple: false,
    showYear: false,
    showMonth: false,
    showBuildingAll: false,
    showMeterAll: false,
    showEnergyTypeAll: false,
    showGasUnit: false,
    showGasUnitDay: false,
    showShortSegment: false,
    showReportType: false,
    showConsumptionOrCo2: false,
    showStatus: false,
    status: false,
  };

  //#region React Hooks (useMemo)

  // Use props directly for show flags to avoid unnecessary state management
  const showCountry = props.showCountry ?? propsDefault.showCountry;
  const showBuilding = props.showBuilding ?? propsDefault.showBuilding;
  const showMeter = props.showMeter ?? propsDefault.showMeter;
  const showEnergyType = props.showEnergyType ?? propsDefault.showEnergyType;
  const showDateSingle = props.showDateSingle ?? propsDefault.showDateSingle;
  const showDateMultiple =
    props.showDateMultiple ?? propsDefault.showDateMultiple;
  const showYear = props.showYear ?? propsDefault.showYear;
  const showMonth = props.showMonth ?? propsDefault.showMonth;
  const showBuildingAll = props.showBuildingAll ?? propsDefault.showBuildingAll;
  const showMeterAll = props.showMeterAll ?? propsDefault.showMeterAll;
  const showEnergyTypeAll =
    props.showEnergyTypeAll ?? propsDefault.showEnergyTypeAll;
  const showGasUnit = props.showGasUnit ?? propsDefault.showGasUnit;
  const showGasUnitDay = props.showGasUnitDay ?? propsDefault.showGasUnitDay;
  const showStatus = props.showStatus ?? propsDefault.showStatus;
  const showShortSegment =
    props.showShortSegment ?? propsDefault.showShortSegment;
  const showReportType = props.showReportType ?? propsDefault.showReportType;
  const showConsumptionOrCo2 =
    props.showConsumptionOrCo2 ?? propsDefault.showConsumptionOrCo2;

  // Determine page settings keys from props or pathname
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  let pastKey = props.pastKey;
  let futureKey = props.futureKey;

  if (!pastKey || !futureKey) {
    if (props.page === "energy" || pathname.includes("/energy")) {
      pastKey = pastKey || "energy-date-past-years";
      futureKey = futureKey || "energy-date-future-years";
    } else if (
      props.page === "climate" ||
      pathname.includes("/climate") ||
      pathname.includes("/carbon")
    ) {
      pastKey = pastKey || "climate-date-past-years";
      futureKey = futureKey || "climate-date-future-years";
    } else if (
      props.page === "procurement" ||
      pathname.includes("/procurement") ||
      pathname.includes("/cost") ||
      pathname.includes("/price") ||
      pathname.includes("/tariff")
    ) {
      pastKey = pastKey || "cost-date-past-years";
      futureKey = futureKey || "cost-date-future-years";
    } else if (props.page === "budget" || pathname.includes("/budget")) {
      pastKey = pastKey || "budget-date-past-years";
      futureKey = futureKey || "budget-date-future-years";
    }
  }

  const { pastYears: fetchedPastYears, futureYears: fetchedFutureYears } =
    useDateRangeSettings(pastKey, futureKey);

  const pastYears = props.pastYears ?? fetchedPastYears;
  const futureYears = props.futureYears ?? fetchedFutureYears;
  const minDate = props.minDate || getMinDate(pastYears);
  const maxDate = props.maxDate || getMaxDate(futureYears);

  // Calculate show props using direct boolean conversion
  const showDateMultipleHourly = Boolean(props.showDateMultipleHourly);
  const showDateMultipleDaily = Boolean(props.showDateMultipleDaily);
  const showDateMultipleMonthly = Boolean(props.showDateMultipleMonthly);
  const showDateSingleHourly = Boolean(props.showDateSingleHourly);
  const showDateSingleDaily = Boolean(props.showDateSingleDaily);
  const showDateSingleMonthly = Boolean(props.showDateSingleMonthly);

  const currentTeam = props.currentTeam ?? undefined;
  const currentSubsidiary = props.currentSubsidiary ?? undefined;

  const currentTeamId = props.currentTeamId ?? user?.currentTeamId ?? undefined;
  const currentSubsidiaryId =
    props.currentSubsidiaryId ?? user?.currentSubsidiaryId ?? undefined;

  //#endregion React Hooks (useMemo)

  // const [currentTeamId, setCurrentTeamId] = useState(props.currentTeamId);
  // const [currentSubsidiaryId, setCurrentSubsidiaryId] = useState(props.currentSubsidiaryId);
  const [country, setCountry] = useState(
    typeof props.country === "undefined" ? propsDefault.country : props.country,
  );
  const [building, setBuilding] = useState(
    typeof props.building === "undefined"
      ? propsDefault.building
      : props.building,
  );
  const [meter, setMeter] = useState(props.meter || propsDefault.meter);
  const [energyType, setEnergyType] = useState(
    typeof props.energyType === "undefined"
      ? propsDefault.energyType
      : props.energyType,
  );
  const [consumptionOrCo2, setConsumptionOrCo2] = useState(
    typeof props.consumptionOrCo2 === "undefined"
      ? propsDefault.consumptionOrCo2
      : props.consumptionOrCo2,
  );
  const [startDate, setStartDate] = useState(
    typeof props.startDate === "undefined"
      ? propsDefault.selectedDate.startDate
      : new Date(props.startDate),
  );
  const [endDate, setEndDate] = useState(
    typeof props.endDate === "undefined"
      ? propsDefault.selectedDate.endDate
      : props.endDate,
  );
  const [selectedYearDate, setSelectedYearDate] = useState(
    typeof props.selectedYearDate === "undefined"
      ? propsDefault.selectedYearDate
      : props.selectedYearDate,
  );
  const [singleDate, setSingleDate] = useState(
    typeof props.singleDate === "undefined"
      ? propsDefault.singleDate
      : props.singleDate,
  );
  const [selectedMonth, setSelectedMonth] = useState(
    typeof props.selectedMonth === "undefined"
      ? propsDefault.selectedMonth
      : props.selectedMonth,
  );
  const [segment, setSegment] = useState(
    typeof props.segment === "undefined"
      ? propsDefault.segmentName
      : props.segment,
  );
  const [dateRange, setDateRange] = useState({
    startDate: startDate,
    endDate: endDate,
  });
  const [status, setStatus] = useState(
    typeof props.status === "undefined" ? propsDefault.status : props.status,
  );
  const [shortSegment, setShortSegment] = useState(
    typeof props.shortSegment === "undefined"
      ? propsDefault.shortSegment
      : props.shortSegment,
  );
  const [reportType, setReportType] = useState(
    typeof props.reportType === "undefined"
      ? propsDefault.reportType
      : props.reportType,
  );
  const [unit, setUnit] = useState(() => {
    if (props.unit) return props.unit;
    const et = (
      typeof props.energyType === "string"
        ? props.energyType
        : props.energyType?.value || ""
    )
      .toString()
      .toLowerCase()
      .trim();
    return et === "gas" || et === "natural gas" ? "m3" : "kwh";
  });
  const [day, setDay] = useState(props.day || "none");
  //endregion React Hooks (useState)

  const isGasEnergyType = useMemo(() => {
    const et = (
      typeof energyType === "string"
        ? energyType
        : energyType?.value || energyType?.label || ""
    )
      .toString()
      .toLowerCase()
      .trim();
    return et === "gas" || et === "natural gas";
  }, [energyType]);

  //region React Hooks (useEffect)

  useEffect(() => {
    if (typeof props.getSelectedValues === "function") {
      props.getSelectedValues({
        building,
        meter,
        energyType,
        consumptionOrCo2,
        date: dateRange,
        segment,
        shortSegment,
        reportType,
        selectedYearDate,
        selectedMonth,
        country: country?.value || country,
        unit,
        day,
      });
    }
  }, [
    building,
    dateRange,
    energyType,
    meter,
    segment,
    shortSegment,
    reportType,
    selectedYearDate,
    selectedMonth,
    props,
    consumptionOrCo2,
    country,
    unit,
    day,
  ]);

  // useEffect(() => {
  //   if (typeof props.currentTeam !== "undefined") {
  //     setTimeout(() => {
  //       setCurrentTeamId(props.currentTeam);
  //     }, 0);
  //   }
  // }, [props.currentTeam]);

  // useEffect(() => {
  //   if (typeof props.currentSubsidiary !== "undefined") {
  //     setTimeout(() => {
  //       setCurrentSubsidiary(props.currentSubsidiary);
  //     }, 0);
  //   }
  // }, [props.currentSubsidiary]);

  useEffect(() => {
    if (typeof props.country !== "undefined") {
      setTimeout(() => {
        setCountry(props.country);
      }, 0);
    }
  }, [props.country]);

  useEffect(() => {
    if (typeof props.building !== "undefined") {
      setTimeout(() => {
        setBuilding(props.building);
      }, 0);
    }
  }, [props.building]);

  useEffect(() => {
    if (typeof props.meter !== "undefined") {
      setTimeout(() => {
        setMeter(props.meter);
      }, 0);
    }
  }, [props.meter]);

  useEffect(() => {
    if (typeof props.unit !== "undefined") {
      setUnit(props.unit);
    }
  }, [props.unit]);

  useEffect(() => {
    if (typeof props.energyType !== "undefined") {
      setTimeout(() => {
        setEnergyType(props.energyType);
        const et = (
          typeof props.energyType === "string"
            ? props.energyType
            : props.energyType?.value || ""
        )
          .toString()
          .toLowerCase()
          .trim();
        if (et === "gas" || et === "natural gas") {
          setUnit("m3");
        }
      }, 0);
    }
  }, [props.energyType]);

  useEffect(() => {
    if (startDate && endDate) {
      setTimeout(() => {
        setDateRange({
          startDate: new Date(startDate),
          endDate: new Date(endDate),
        });
      }, 0);
    }
  }, [endDate, startDate]);

  useEffect(() => {
    if (props.singleDate) {
      setTimeout(() => {
        setSingleDate(props.singleDate);
      }, 0);
    }
  }, [props.singleDate]);

  useEffect(() => {
    if (props.startDate) {
      setTimeout(() => {
        setStartDate(new Date(props.startDate));
      }, 0);
    }
  }, [props.startDate]);

  useEffect(() => {
    if (props.endDate) {
      setTimeout(() => {
        setEndDate(new Date(props.endDate));
      }, 0);
    }
  }, [props.endDate]);

  useEffect(() => {
    if (typeof props.segment !== "undefined") {
      setTimeout(() => {
        setSegment(props.segment);
      }, 0);
    }
  }, [props.segment]);

  useEffect(() => {
    if (typeof props.shortSegment !== "undefined") {
      setTimeout(() => {
        setShortSegment(props.shortSegment);
      }, 0);
    }
  }, [props.shortSegment]);

  useEffect(() => {
    if (typeof props.reportType !== "undefined") {
      setTimeout(() => {
        setReportType(props.reportType);
      }, 0);
    }
  }, [props.reportType]);

  //endregion React Hooks (useEffect)

  //region Handlers
  const hitGetSelectedValues = () => {
    // if (typeof props.getSelectedValues === "function") {
    //   props.getSelectedValues({
    //     building,
    //     meter,
    //     energyType,
    //     consumptionOrCo2,
    //     date: dateRange,
    //     segment,
    //     shortSegment,
    //     reportType,
    //     selectedYearDate,
    //     selectedMonth,
    //   });
    // }
  };

  const handleBuildingChange = (event: any) => {
    setBuilding(event);
    hitGetSelectedValues();
  };

  const handleMeterChange = (event: any) => {
    setMeter(event);
    hitGetSelectedValues();
  };

  const handleEnergyTypeChange = (event: any) => {
    setEnergyType(event);
    const eventVal =
      typeof event === "string" ? event : event?.value || event?.label || "";
    const etVal = eventVal.toString().toLowerCase().trim();
    if (etVal === "gas" || etVal === "natural gas") {
      setUnit("m3");
    } else {
      setUnit("kwh");
    }
    hitGetSelectedValues();
  };

  const handleStatusChange = (event: any) => {
    setStatus(event);
    hitGetSelectedValues();
  };

  const handleShortSegmentChange = (event: any) => {
    setShortSegment(event);
    hitGetSelectedValues();
  };

  const handleEnergyReportTypeChange = (event: any) => {
    setEnergyType(event);
    hitGetSelectedValues();
  };

  const handleConsumptionOrCo2DChange = (event: any) => {
    setConsumptionOrCo2(event);
    hitGetSelectedValues();
  };

  const handleMonthChange = (event: any) => {
    if (!isEqual(selectedMonth, event)) {
      setSelectedMonth(event);
    }
    hitGetSelectedValues();
  };

  const handleGetSelectedDateSingle = (
    selectedDate: Date | string,
    selectedSegment: SegmentKey,
  ) => {
    const { startDate: startDate1, endDate: endDate1 } = getStartAndEndDate(
      selectedDate,
      selectedSegment,
    );

    if (selectedSegment !== segment) {
      setShortSegment(segments[selectedSegment].shortSegment);
    }

    if (!isEqual(startDate, startDate1)) {
      setStartDate(startDate1);
    }
    if (!isEqual(endDate, endDate1)) {
      setEndDate(endDate1);
    }
    if (selectedSegment !== segment) {
      setSegment(selectedSegment);
    }
    hitGetSelectedValues();
  };

  const handleGetSelectedDateMulti = (
    dateArray: { startDate: Date; endDate: Date },
    selectedSegment: SegmentKey,
  ) => {
    const { startDate: startDate1, endDate: endDate1 } = dateArray;

    if (selectedSegment !== segment) {
      setShortSegment(segments[selectedSegment].shortSegment);
    }

    if (!isEqual(startDate, startDate1)) {
      setStartDate(startDate1);
    }
    if (!isEqual(endDate1, endDate1)) {
      setEndDate(endDate1);
    }
    setSegment(selectedSegment);
    hitGetSelectedValues();
  };

  const handleGetSelectedYearDate = (date: Date | string) => {
    setSelectedYearDate(date);
    hitGetSelectedValues();
  };

  const handleCountryChange = (event: any) => {
    setCountry(event);
    hitGetSelectedValues();
  };

  const handleUnitChange = (option: any) => {
    const val = (option?.value || option || "").toString().toLowerCase();
    setUnit(val);
    hitGetSelectedValues();
  };

  const handleDayChange = (option: any) => {
    setDay(option?.value || option);
    hitGetSelectedValues();
  };
  //endregion Handlers

  return (
    <Grid container spacing={3} sx={{ width: "100%" }}>
      <Grid size={{ xs: 12 }}>
        <FilterBox>
          <Grid
            container
            spacing={1}
            sx={{ width: "100%", alignItems: "center" }}
          >
            {showCountry && (
              <>
                <Grid size={{ xs: 12, sm: "auto" }}>
                  <CustomersCountryDropdown
                    defaultValue={country?.value || country || ""}
                    required
                    customerId={currentTeamId}
                    subsidiaryId={currentSubsidiaryId}
                    onChange={handleCountryChange}
                    sx={{
                      maxWidth: 200,
                    }}
                  />
                </Grid>

                {/* <Grid size={{xs: 12, sm: "auto"}}>
                <CountryDropdown
                  defaultValue={country?.value || country || ""}
                  required
                  currentTeam={currentTeam}
                  currentSubsidiary={currentSubsidiary}
                  onChange={handleCountryChange}
                  sx={{
                    maxWidth: 200,
                  }}
                />
              </Grid> */}
              </>
            )}
            {showEnergyType && (
              <Grid size={{ xs: 12, sm: "auto" }}>
                <EnergyTypeDropdown
                  onChange={handleEnergyTypeChange}
                  currentTeam={currentTeam}
                  currentSubsidiary={currentSubsidiary}
                  country={country?.value || country || ""}
                  defaultValue={energyType ? energyType.value : energyType}
                  showAll={showEnergyTypeAll}
                  required
                  sx={{
                    maxWidth: 200,
                  }}
                />
              </Grid>
            )}
            {showConsumptionOrCo2 && (
              <Grid size={{ xs: 12, sm: "auto" }}>
                <ConsumptionOrCo2Dropdown
                  onChange={handleConsumptionOrCo2DChange}
                  defaultValue={
                    consumptionOrCo2 ? consumptionOrCo2.value : consumptionOrCo2
                  }
                  sx={{
                    maxWidth: 200,
                  }}
                />
              </Grid>
            )}
            {showBuilding && (
              <Grid size={{ xs: 12, sm: "auto" }}>
                <BuildingDropdown
                  onChange={handleBuildingChange}
                  energyType={energyType}
                  defaultValue={building?.value ? building.value : building}
                  currentTeam={currentTeam}
                  currentSubsidiary={currentSubsidiary}
                  required
                  showAll={showBuildingAll}
                  sx={{
                    maxWidth: 200,
                  }}
                />
              </Grid>
            )}
            {showMeter && (
              <Grid size={{ xs: 12, sm: "auto" }}>
                <MeterDropdown
                  onChange={handleMeterChange}
                  energyType={energyType?.value ? energyType.value : energyType}
                  defaultValue={meter?.value ? meter.value : meter}
                  building={building?.value ? building.value : building}
                  currentTeam={currentTeam}
                  currentSubsidiary={currentSubsidiary}
                  showAll={showMeterAll}
                  visualStyle="simple"
                  sx={{
                    maxWidth: 200,
                  }}
                />
              </Grid>
            )}
            {(showGasUnit || showGasUnitDay) && isGasEnergyType && (
              <Grid size={{ xs: 12, sm: "auto" }}>
                <SelectDropdown
                  key={`unit-dropdown-${typeof energyType === "string" ? energyType : energyType?.value || "gas"}`}
                  componentLabel="Unit"
                  options={[
                    { value: "m3", label: "M3" },
                    { value: "kwh", label: "kWh" },
                  ]}
                  defaultValue={unit}
                  onChange={handleUnitChange}
                  visualStyle="simple"
                  disableCache
                  sx={{ maxWidth: 150 }}
                />
              </Grid>
            )}
            {showGasUnitDay && isGasEnergyType && (
              <Grid size={{ xs: 12, sm: "auto" }}>
                <SelectDropdown
                  componentLabel="Day"
                  options={[
                    { value: "none", label: "None" },
                    { value: "day", label: "Day" },
                  ]}
                  defaultValue={day}
                  onChange={handleDayChange}
                  visualStyle="simple"
                  sx={{ maxWidth: 150 }}
                />
              </Grid>
            )}
            {showStatus && (
              <Grid size={{ xs: 12, sm: "auto" }}>
                <StatusDropdown
                  onChange={handleStatusChange}
                  defaultValue={status?.value ? status.value : status}
                  showAll={showStatus}
                  visualStyle="old"
                />
              </Grid>
            )}
            <Grid size={{ xs: 1, sm: "grow" }} sx={{ textAlign: "end" }}></Grid>
            {showDateMultiple && (
              <Grid size={{ xs: 4, sm: "grow" }} sx={{ textAlign: "end" }}>
                <MultiDatePickerDmy
                  getSelectedDate={handleGetSelectedDateMulti}
                  selectedDate={dateRange}
                  segment={segment}
                  futureDates={futureYears}
                  pastYears={pastYears}
                  futureYears={futureYears}
                  minDate={minDate}
                  maxDate={maxDate}
                  showHourly={showDateMultipleHourly}
                  showDaily={showDateMultipleDaily}
                  showMonthly={showDateMultipleMonthly}
                />
              </Grid>
            )}
            {showDateSingle && (
              <Grid size={{ xs: 4, sm: "grow" }} sx={{ textAlign: "end" }}>
                <SingleDatePickerDmy
                  getSelectedDate={handleGetSelectedDateSingle}
                  selectedDate={singleDate}
                  segment={segment}
                  futureDates={futureYears}
                  pastYears={pastYears}
                  futureYears={futureYears}
                  minDate={minDate}
                  maxDate={maxDate}
                  showHourly={showDateSingleHourly}
                  showDaily={showDateSingleDaily}
                  showMonthly={showDateSingleMonthly}
                />
              </Grid>
            )}
            {showMonth && (
              <Grid size={{ xs: 4, sm: "auto" }} sx={{ textAlign: "end" }}>
                <MonthsDropdown
                  defaultValue={""}
                  visualStyle={"autoComplete"}
                  onChange={handleMonthChange}
                />
              </Grid>
            )}
            {showYear && (
              <Grid size={{ xs: 12, sm: "auto" }} sx={{ textAlign: "end" }}>
                <YearPicker
                  getSelectedYearDate={handleGetSelectedYearDate}
                  selectedYearDate={selectedYearDate}
                  futureDates={futureYears}
                  pastYears={pastYears}
                  futureYears={futureYears}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              </Grid>
            )}
            {showReportType && (
              <Grid size={{ xs: 12, sm: "auto" }} sx={{ textAlign: "end" }}>
                <EnergyReportTypeDropdown
                  onChange={handleEnergyReportTypeChange}
                  defaultValue={
                    reportType?.value ? reportType.value : reportType
                  }
                />
              </Grid>
            )}
            {showShortSegment && (
              <Grid size={{ xs: 12, sm: "auto" }} sx={{ textAlign: "end" }}>
                <SegmentDropDown
                  onChange={handleShortSegmentChange}
                  defaultValue={
                    shortSegment?.value ? shortSegment.value : shortSegment
                  }
                />
              </Grid>
            )}
          </Grid>
        </FilterBox>
      </Grid>
    </Grid>
  );
};

FilterBar1.propTypes = {
  singleDate: instanceOf(Date),
  startDate: instanceOf(Date),
  endDate: instanceOf(Date),
  currentTeam: string,
  currentSubsidiary: string,
  country: string,
  building: string,
  meter: string,
  energyType: string,
  segment: string,
  shortSegment: string,
  reportType: string,
  showCountry: bool,
  showBuilding: bool,
  showMeter: bool,
  showEnergyType: bool,
  status: bool,
  showDateSingle: bool,
  showDateSingleHourly: bool,
  showDateSingleDaily: bool,
  showDateSingleMonthly: bool,
  showDateMultiple: bool,
  showDateMultipleHourly: bool,
  showDateMultipleDaily: bool,
  showDateMultipleMonthly: bool,
  showYear: bool,
  showMonth: bool,
  showBuildingAll: bool,
  showMeterAll: bool,
  showEnergyTypeAll: bool,
  showGasUnit: bool,
  showGasUnitDay: bool,
  showShortSegment: bool,
  showReportType: bool,
  showConsumptionOrCo2: bool,
  getSelectedValues: func.isRequired,
  showStatus: bool,
  consumptionOrCo2: string,
  selectedYearDate: instanceOf(Date),
  selectedMonth: string,
};
